import {
  type BindableQrTargetType,
  QR_BINDINGS_BY_TARGETS_MAX_IDS,
  type QrBindingForTargetDto,
  type QrBindingsByTargetsResponseDto,
  type QrTargetType,
} from '@prospero/shared';
import { inject } from 'inversify';
import { Injectable } from '../../decorators/injectable';
import { chunk } from '../../utils/collections';
import { getHttpErrorStatus } from '../../utils/error.utils';
import { HttpService, type IHttpService } from '../http/http.service';
import type { IQrBindingService, QrBindingResolveResult } from './qr-binding.service.interface';

@Injectable()
export class QrBindingService implements IQrBindingService {
  constructor(@inject(HttpService) private readonly http: IHttpService) {}

  async bind(input: {
    qrUuid: string;
    targetType: BindableQrTargetType;
    targetId: string;
  }): Promise<void> {
    await this.http.post('qr-bindings', input);
  }

  async unbind(qrUuid: string): Promise<void> {
    await this.http.delete(`qr-bindings/${qrUuid}`);
  }

  async resolve(qrUuid: string, signal?: AbortSignal): Promise<QrBindingResolveResult | null> {
    try {
      return await this.http.get<QrBindingResolveResult>(`qr-bindings/resolve/${qrUuid}`, {
        signal,
      });
    } catch (err) {
      const status = getHttpErrorStatus(err);
      if (status === 404) {
        return null;
      }
      throw err;
    }
  }

  async getByTarget(
    targetType: QrTargetType,
    targetId: string,
  ): Promise<{ qrUuid: string | null }> {
    return this.http.get(`qr-bindings/by-target?type=${targetType}&id=${targetId}`);
  }

  async getByTargets(
    targetType: BindableQrTargetType,
    targetIds: readonly string[],
  ): Promise<QrBindingForTargetDto[]> {
    const responses = await Promise.all(
      chunk(targetIds, QR_BINDINGS_BY_TARGETS_MAX_IDS).map((targetIdChunk) => {
        const query = new URLSearchParams({ type: targetType, ids: targetIdChunk.join(',') });
        return this.http.get<QrBindingsByTargetsResponseDto>(`qr-bindings/by-targets?${query}`);
      }),
    );
    return responses.flatMap((response) => response.bindings);
  }
}
